import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  CryptoIcon, FixedDepositIcon, BankIcon, OverseasIcon,
  LandIcon, InsuranceIcon, StocksIcon, MoneyLentIcon, SunIcon, MoonIcon,
  LogoutIcon, PlusIcon, TrashIcon, TrendUpIcon, TrendDownIcon, WalletIcon, EditIcon
} from './Icons';
import {
  cryptoApi, fixedDepositsApi, bankBalanceApi, overseasApi,
  landApi, insuranceApi, stocksApi, moneyLentApi, dashboardApi
} from '../services/api';
import WealthChart from './WealthChart';
import './Dashboard.css';

const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Portfolio Section Component
const PortfolioSection = ({ 
  title, 
  icon: Icon, 
  items, 
  total, 
  color, 
  onAdd, 
  onEdit,
  onDelete,
  renderItem,
  addForm 
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const handleEditClick = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleFormSubmit = (data) => {
    if (editingItem) {
      onEdit(editingItem.id, data);
      setEditingItem(null);
    } else {
      onAdd(data);
    }
    setShowForm(false);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  return (
    <div className={`portfolio-section ${color}`}>
      <div className="section-header">
        <div className="section-title">
          <div className="section-icon">
            <Icon size={24} />
          </div>
          <div>
            <h3>{title}</h3>
            <p className="section-total">{formatCurrency(total)}</p>
          </div>
        </div>
        <button className="add-btn" onClick={() => { setEditingItem(null); setShowForm(!showForm); }}>
          <PlusIcon size={18} />
        </button>
      </div>

      {showForm && (
        <div className="add-form" key={editingItem?.id || 'new'}>
          {React.createElement(addForm, {
            onSubmit: handleFormSubmit,
            onCancel: handleFormCancel,
            initialData: editingItem,
            isEdit: !!editingItem
          })}
        </div>
      )}

      <div className="section-items">
        {items.length === 0 ? (
          <p className="no-items">No items added yet</p>
        ) : (
          items.map((item) => (
            <div key={item.id} className="item-card">
              {renderItem(item)}
              <div className="item-actions">
                <button className="edit-btn" onClick={() => handleEditClick(item)}>
                  <EditIcon size={16} />
                </button>
                <button className="delete-btn" onClick={() => onDelete(item.id)}>
                  <TrashIcon size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Form Components for each section
const CryptoForm = ({ onSubmit, onCancel, initialData, isEdit }) => {
  const [form, setForm] = useState(
    initialData 
      ? { 
          wallet_address: initialData.wallet_address || '', 
          coin_name: initialData.coin_name || '', 
          coin_symbol: initialData.coin_symbol || '', 
          amount: initialData.amount != null ? String(initialData.amount) : '', 
          current_price: (initialData.current_price != null ? String(initialData.current_price) : initialData.purchase_price != null ? String(initialData.purchase_price) : '')
        }
      : { wallet_address: '', coin_name: '', coin_symbol: '', amount: '', current_price: '' }
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="form-grid">
      <input placeholder="Wallet Address" value={form.wallet_address} onChange={e => setForm({...form, wallet_address: e.target.value})} />
      <input placeholder="Coin Name" value={form.coin_name} onChange={e => setForm({...form, coin_name: e.target.value})} required />
      <input placeholder="Symbol (BTC)" value={form.coin_symbol} onChange={e => setForm({...form, coin_symbol: e.target.value})} required />
      <input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required step="any" />
      <input type="number" placeholder="Current Price" value={form.current_price} onChange={e => setForm({...form, current_price: e.target.value})} step="any" />
      <div className="form-actions">
        <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Add'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const FDForm = ({ onSubmit, onCancel, initialData, isEdit }) => {
  const [form, setForm] = useState(() => {
    if (!initialData) {
      return { bank_name: '', amount: '', interest_rate: '', maturity_date: '' };
    }
    let maturityDate = '';
    if (initialData.maturity_date) {
      try {
        const date = new Date(initialData.maturity_date);
        if (!isNaN(date.getTime())) {
          maturityDate = date.toISOString().split('T')[0];
        }
      } catch (e) {
        // Invalid date, leave empty
      }
    }
    return {
      bank_name: initialData.bank_name || '',
      amount: initialData.amount != null ? String(initialData.amount) : '',
      interest_rate: initialData.interest_rate != null ? String(initialData.interest_rate) : '',
      maturity_date: maturityDate
    };
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="form-grid">
      <input placeholder="Bank Name" value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} required />
      <input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required />
      <input type="number" placeholder="Interest Rate %" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} step="0.01" />
      <input type="date" placeholder="Maturity Date" value={form.maturity_date} onChange={e => setForm({...form, maturity_date: e.target.value})} required />
      <div className="form-actions">
        <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Add'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const BankForm = ({ onSubmit, onCancel, initialData, isEdit }) => {
  const [form, setForm] = useState(
    initialData
      ? { bank_name: initialData.bank_name || '', account_type: initialData.account_type || '', amount: initialData.amount != null ? String(initialData.amount) : '' }
      : { bank_name: '', account_type: '', amount: '' }
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="form-grid">
      <input placeholder="Bank Name" value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} required />
      <input placeholder="Account Type" value={form.account_type} onChange={e => setForm({...form, account_type: e.target.value})} />
      <input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required />
      <div className="form-actions">
        <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Add'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const OverseasForm = ({ onSubmit, onCancel, initialData, isEdit }) => {
  const [form, setForm] = useState(
    initialData
      ? { platform_name: initialData.platform_name || '', amount: initialData.amount != null ? String(initialData.amount) : '', currency: initialData.currency || 'USD' }
      : { platform_name: '', amount: '', currency: 'USD' }
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="form-grid">
      <input placeholder="Platform Name" value={form.platform_name} onChange={e => setForm({...form, platform_name: e.target.value})} required />
      <input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required />
      <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
        <option value="USD">USD</option>
        <option value="EUR">EUR</option>
        <option value="GBP">GBP</option>
      </select>
      <div className="form-actions">
        <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Add'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const LandForm = ({ onSubmit, onCancel, initialData, isEdit }) => {
  const [form, setForm] = useState(
    initialData
      ? { location: initialData.location || '', amount: initialData.amount != null ? String(initialData.amount) : '', property_type: initialData.property_type || '' }
      : { location: '', amount: '', property_type: '' }
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="form-grid">
      <input placeholder="Location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} required />
      <input type="number" placeholder="Value" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required />
      <input placeholder="Property Type" value={form.property_type} onChange={e => setForm({...form, property_type: e.target.value})} />
      <div className="form-actions">
        <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Add'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const InsuranceForm = ({ onSubmit, onCancel, initialData, isEdit }) => {
  const [form, setForm] = useState(
    initialData
      ? { company_name: initialData.company_name || '', policy_type: initialData.policy_type || '', amount: initialData.amount != null ? String(initialData.amount) : '', premium: initialData.premium != null ? String(initialData.premium) : '' }
      : { company_name: '', policy_type: '', amount: '', premium: '' }
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="form-grid">
      <input placeholder="Company Name" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} required />
      <input placeholder="Policy Type" value={form.policy_type} onChange={e => setForm({...form, policy_type: e.target.value})} />
      <input type="number" placeholder="Coverage Amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required />
      <input type="number" placeholder="Annual Premium" value={form.premium} onChange={e => setForm({...form, premium: e.target.value})} />
      <div className="form-actions">
        <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Add'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const StocksForm = ({ onSubmit, onCancel, initialData, isEdit }) => {
  const [form, setForm] = useState(
    initialData
      ? { broker_name: initialData.broker_name || '', amount: initialData.amount != null ? String(initialData.amount) : '' }
      : { broker_name: '', amount: '' }
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="form-grid">
      <input placeholder="Broker Name" value={form.broker_name} onChange={e => setForm({...form, broker_name: e.target.value})} required />
      <input type="number" placeholder="Portfolio Value" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required />
      <div className="form-actions">
        <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Add'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const MoneyLentForm = ({ onSubmit, onCancel, initialData, isEdit }) => {
  const [form, setForm] = useState(
    initialData
      ? { person_name: initialData.person_name || '', amount: initialData.amount != null ? String(initialData.amount) : '' }
      : { person_name: '', amount: '' }
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="form-grid">
      <input placeholder="Name of person" value={form.person_name} onChange={e => setForm({...form, person_name: e.target.value})} required />
      <input type="number" placeholder="Amount lent" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required step="any" />
      <div className="form-actions">
        <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Add'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const Dashboard = () => {
  const auth = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  // Extract user safely with optional chaining
  const user = auth?.user;
  const logout = auth?.logout;
  
  // All hooks must be called before any conditional returns
  const [data, setData] = useState({
    crypto: [],
    fixedDeposits: [],
    bankBalance: [],
    overseas: [],
    land: [],
    insurance: [],
    stocks: [],
    moneyLent: [],
  });
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [wealthHistory, setWealthHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all portfolio data
  const fetchAllData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const [crypto, fixedDeposits, bankBalance, overseas, land, insurance, stocks, moneyLent, summary, history] = await Promise.all([
        cryptoApi.getAll().catch(() => []),
        fixedDepositsApi.getAll().catch(() => []),
        bankBalanceApi.getAll().catch(() => []),
        overseasApi.getAll().catch(() => []),
        landApi.getAll().catch(() => []),
        insuranceApi.getAll().catch(() => []),
        stocksApi.getAll().catch(() => []),
        moneyLentApi.getAll().catch(() => []),
        dashboardApi.getSummary().catch(() => null),
        dashboardApi.getWealthHistory().catch(() => []),
      ]);

      setData({
        crypto: crypto || [],
        fixedDeposits: fixedDeposits || [],
        bankBalance: bankBalance || [],
        overseas: overseas || [],
        land: land || [],
        insurance: insurance || [],
        stocks: stocks || [],
        moneyLent: moneyLent || [],
      });
      setDashboardSummary(summary);
      setWealthHistory(history || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorMessage = err?.message || err?.toString() || 'Failed to load portfolio data. Please check if the backend is running.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Calculate totals from dashboard summary or fallback to local calculation
  const totals = dashboardSummary ? {
    crypto: dashboardSummary.totals.crypto || 0,
    fixedDeposits: dashboardSummary.totals.fixedDeposits || 0,
    bankBalance: dashboardSummary.totals.bankBalance || 0,
    overseas: dashboardSummary.totals.overseas || 0,
    land: dashboardSummary.totals.land || 0,
    insurance: dashboardSummary.totals.insurance || 0,
    stocks: dashboardSummary.totals.stocks || 0,
    moneyLent: dashboardSummary.totals.moneyLent || 0,
    total: dashboardSummary.totalWealth || 0,
  } : {
    crypto: 0,
    fixedDeposits: 0,
    bankBalance: 0,
    overseas: 0,
    land: 0,
    insurance: 0,
    stocks: 0,
    moneyLent: 0,
    total: 0,
  };
  
  // Calculate change from dashboard summary or history
  const wealthChange = dashboardSummary?.change || 0;
  const wealthChangePercent = dashboardSummary?.changePercent || 0;

  // Generic add handler
  const handleAdd = async (category, newItem) => {
    if (!user?.id) return;

    try {
      const apiMap = {
        crypto: cryptoApi,
        fixedDeposits: fixedDepositsApi,
        bankBalance: bankBalanceApi,
        overseas: overseasApi,
        land: landApi,
        insurance: insuranceApi,
        stocks: stocksApi,
        moneyLent: moneyLentApi,
      };

      const api = apiMap[category];
      if (!api) return;

      const itemData = { ...newItem };
      
      // Convert amount to number for numeric fields
      if (itemData.amount) itemData.amount = parseFloat(itemData.amount);
      if (itemData.current_price) itemData.current_price = parseFloat(itemData.current_price);
      if (itemData.purchase_price) itemData.purchase_price = parseFloat(itemData.purchase_price);
      if (itemData.interest_rate) itemData.interest_rate = parseFloat(itemData.interest_rate);
      if (itemData.premium) itemData.premium = parseFloat(itemData.premium);

      await api.add(itemData);
      
      // Refresh data after adding
      await fetchAllData();
    } catch (err) {
      console.error(`Error adding ${category}:`, err);
      const errorMessage = err?.message || err?.toString() || `Failed to add ${category} item. Please try again.`;
      setError(errorMessage);
    }
  };

  // Generic delete handler
  const handleDelete = async (category, id) => {
    try {
      const apiMap = {
        crypto: cryptoApi,
        fixedDeposits: fixedDepositsApi,
        bankBalance: bankBalanceApi,
        overseas: overseasApi,
        land: landApi,
        insurance: insuranceApi,
        stocks: stocksApi,
        moneyLent: moneyLentApi,
      };

      const api = apiMap[category];
      if (!api) return;

      await api.delete(id);
      
      // Refresh data after deleting
      await fetchAllData();
    } catch (err) {
      console.error(`Error deleting ${category}:`, err);
      const errorMessage = err?.message || err?.toString() || `Failed to delete ${category} item. Please try again.`;
      setError(errorMessage);
    }
  };

  // Generic edit handler
  const handleEdit = async (category, id, updatedItem) => {
    if (!user?.id) return;

    try {
      const apiMap = {
        crypto: cryptoApi,
        fixedDeposits: fixedDepositsApi,
        bankBalance: bankBalanceApi,
        overseas: overseasApi,
        land: landApi,
        insurance: insuranceApi,
        stocks: stocksApi,
        moneyLent: moneyLentApi,
      };

      const api = apiMap[category];
      if (!api) return;

      const itemData = { ...updatedItem };
      
      // Convert amount to number for numeric fields
      if (itemData.amount) itemData.amount = parseFloat(itemData.amount);
      if (itemData.current_price) itemData.current_price = parseFloat(itemData.current_price);
      if (itemData.purchase_price) itemData.purchase_price = parseFloat(itemData.purchase_price);
      if (itemData.interest_rate) itemData.interest_rate = parseFloat(itemData.interest_rate);
      if (itemData.premium) itemData.premium = parseFloat(itemData.premium);

      await api.update(id, itemData);
      
      // Refresh data after editing
      await fetchAllData();
    } catch (err) {
      console.error(`Error editing ${category}:`, err);
      const errorMessage = err?.message || err?.toString() || `Failed to edit ${category} item. Please try again.`;
      setError(errorMessage);
    }
  };

  // Record current wealth snapshot
  const recordWealthSnapshot = async () => {
    if (!user?.id) return;

    try {
      const snapshotData = {
        total_wealth: totals.total,
        crypto_total: totals.crypto,
        fd_total: totals.fixedDeposits,
        bank_total: totals.bankBalance,
        overseas_total: totals.overseas,
        land_total: totals.land,
        insurance_total: totals.insurance,
        stocks_total: totals.stocks,
      };

      await dashboardApi.recordWealth(snapshotData);
      
      // Refresh data after recording
      await fetchAllData();
    } catch (err) {
      console.error('Error recording wealth snapshot:', err);
      const errorMessage = err?.message || err?.toString() || 'Failed to record wealth snapshot. Please try again.';
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading portfolio data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {error && (
        <div className="error-banner" onClick={() => setError(null)}>
          {typeof error === 'string' ? error : error?.message || error?.toString() || 'An error occurred'}
          <span className="error-close">×</span>
        </div>
      )}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo">
            <WalletIcon size={28} />
          </div>
          <div>
            <h1>Portfolio Dashboard</h1>
            <p>Welcome back, {user?.username}</p>
          </div>
        </div>
        <div className="header-right">
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === 'light' ? <MoonIcon size={20} /> : <SunIcon size={20} />}
          </button>
          <button className="logout-btn" onClick={logout}>
            <LogoutIcon size={20} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        {/* Wealth Overview */}
        <section className="wealth-overview">
          <div className="total-wealth-card">
            <div className="wealth-info">
              <span className="wealth-label">Total Net Worth</span>
              <h2 className="wealth-amount">{formatCurrency(totals.total)}</h2>
              <div className={`wealth-change ${wealthChange >= 0 ? 'positive' : 'negative'}`}>
                {wealthChange >= 0 ? <TrendUpIcon size={16} /> : <TrendDownIcon size={16} />}
                <span>{wealthChange >= 0 ? '+' : ''}{formatCurrency(wealthChange)} ({Math.abs(wealthChangePercent).toFixed(2)}%)</span>
              </div>
            </div>
            <button className="snapshot-btn" onClick={recordWealthSnapshot}>
              Record Snapshot
            </button>
          </div>

          <div className="wealth-breakdown">
            {[
              { label: 'Crypto', value: totals.crypto, color: 'orange' },
              { label: 'Fixed Deposits', value: totals.fixedDeposits, color: 'blue' },
              { label: 'Bank Balance', value: totals.bankBalance, color: 'green' },
              { label: 'Overseas', value: totals.overseas, color: 'purple' },
              { label: 'Land', value: totals.land, color: 'teal' },
              { label: 'Insurance', value: totals.insurance, color: 'pink' },
              { label: 'Stocks', value: totals.stocks, color: 'yellow' },
              { label: 'Money Lent', value: totals.moneyLent, color: 'indigo' },
            ].map(item => (
              <div key={item.label} className={`breakdown-item ${item.color}`}>
                <span className="breakdown-label">{item.label}</span>
                <span className="breakdown-value">{formatCurrency(item.value)}</span>
                <div className="breakdown-bar">
                  <div 
                    className="breakdown-fill" 
                    style={{ width: `${(item.value / totals.total) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Portfolio Sections */}
        <section className="portfolio-grid">
          <PortfolioSection
            title="Crypto Portfolio"
            icon={CryptoIcon}
            items={data.crypto}
            total={totals.crypto}
            color="orange"
            onAdd={(item) => handleAdd('crypto', item)}
            onEdit={(id, item) => handleEdit('crypto', id, item)}
            onDelete={(id) => handleDelete('crypto', id)}
            renderItem={(item) => (
              <div className="item-content">
                <div className="item-main">
                  <span className="item-name">{item.coin_name} ({item.coin_symbol})</span>
                  <span className="item-amount">{item.amount} {item.coin_symbol}</span>
                </div>
                <div className="item-details">
                  <span>@ {formatCurrency(item.current_price || item.purchase_price || 0)}</span>
                  <span className="item-value">{formatCurrency(item.amount * (item.current_price || item.purchase_price || 0))}</span>
                </div>
                {item.wallet_address && <span className="item-meta">{item.wallet_address}</span>}
              </div>
            )}
            addForm={CryptoForm}
          />

          <PortfolioSection
            title="Fixed Deposits"
            icon={FixedDepositIcon}
            items={data.fixedDeposits}
            total={totals.fixedDeposits}
            color="blue"
            onAdd={(item) => handleAdd('fixedDeposits', item)}
            onEdit={(id, item) => handleEdit('fixedDeposits', id, item)}
            onDelete={(id) => handleDelete('fixedDeposits', id)}
            renderItem={(item) => (
              <div className="item-content">
                <div className="item-main">
                  <span className="item-name">{item.bank_name}</span>
                  <span className="item-value">{formatCurrency(item.amount)}</span>
                </div>
                <div className="item-details">
                  {item.interest_rate && <span>{item.interest_rate}% p.a.</span>}
                  <span>Matures: {formatDate(item.maturity_date)}</span>
                </div>
              </div>
            )}
            addForm={FDForm}
          />

          <PortfolioSection
            title="Bank Balance"
            icon={BankIcon}
            items={data.bankBalance}
            total={totals.bankBalance}
            color="green"
            onAdd={(item) => handleAdd('bankBalance', item)}
            onEdit={(id, item) => handleEdit('bankBalance', id, item)}
            onDelete={(id) => handleDelete('bankBalance', id)}
            renderItem={(item) => (
              <div className="item-content">
                <div className="item-main">
                  <span className="item-name">{item.bank_name}</span>
                  <span className="item-value">{formatCurrency(item.amount)}</span>
                </div>
                {item.account_type && <span className="item-meta">{item.account_type}</span>}
              </div>
            )}
            addForm={BankForm}
          />

          <PortfolioSection
            title="Overseas Investments"
            icon={OverseasIcon}
            items={data.overseas}
            total={totals.overseas}
            color="purple"
            onAdd={(item) => handleAdd('overseas', item)}
            onEdit={(id, item) => handleEdit('overseas', id, item)}
            onDelete={(id) => handleDelete('overseas', id)}
            renderItem={(item) => (
              <div className="item-content">
                <div className="item-main">
                  <span className="item-name">{item.platform_name}</span>
                  <span className="item-value">{formatCurrency(item.amount, item.currency)}</span>
                </div>
                <span className="item-meta">≈ {formatCurrency(item.amount * 83, 'INR')} INR</span>
              </div>
            )}
            addForm={OverseasForm}
          />

          <PortfolioSection
            title="Land & Property"
            icon={LandIcon}
            items={data.land}
            total={totals.land}
            color="teal"
            onAdd={(item) => handleAdd('land', item)}
            onEdit={(id, item) => handleEdit('land', id, item)}
            onDelete={(id) => handleDelete('land', id)}
            renderItem={(item) => (
              <div className="item-content">
                <div className="item-main">
                  <span className="item-name">{item.location}</span>
                  <span className="item-value">{formatCurrency(item.amount)}</span>
                </div>
                {item.property_type && <span className="item-meta">{item.property_type}</span>}
              </div>
            )}
            addForm={LandForm}
          />

          <PortfolioSection
            title="Insurance"
            icon={InsuranceIcon}
            items={data.insurance}
            total={totals.insurance}
            color="pink"
            onAdd={(item) => handleAdd('insurance', item)}
            onEdit={(id, item) => handleEdit('insurance', id, item)}
            onDelete={(id) => handleDelete('insurance', id)}
            renderItem={(item) => (
              <div className="item-content">
                <div className="item-main">
                  <span className="item-name">{item.company_name}</span>
                  <span className="item-value">{formatCurrency(item.amount)}</span>
                </div>
                <div className="item-details">
                  {item.policy_type && <span>{item.policy_type}</span>}
                  {item.premium && <span>Premium: {formatCurrency(item.premium)}/yr</span>}
                </div>
              </div>
            )}
            addForm={InsuranceForm}
          />

          <PortfolioSection
            title="Stocks"
            icon={StocksIcon}
            items={data.stocks}
            total={totals.stocks}
            color="yellow"
            onAdd={(item) => handleAdd('stocks', item)}
            onEdit={(id, item) => handleEdit('stocks', id, item)}
            onDelete={(id) => handleDelete('stocks', id)}
            renderItem={(item) => (
              <div className="item-content">
                <div className="item-main">
                  <span className="item-name">{item.broker_name}</span>
                  <span className="item-value">{formatCurrency(item.amount)}</span>
                </div>
              </div>
            )}
            addForm={StocksForm}
          />

          <PortfolioSection
            title="Money Lent"
            icon={MoneyLentIcon}
            items={data.moneyLent}
            total={totals.moneyLent}
            color="indigo"
            onAdd={(item) => handleAdd('moneyLent', item)}
            onEdit={(id, item) => handleEdit('moneyLent', id, item)}
            onDelete={(id) => handleDelete('moneyLent', id)}
            renderItem={(item) => (
              <div className="item-content">
                <div className="item-main">
                  <span className="item-name">{item.person_name}</span>
                  <span className="item-value">{formatCurrency(item.amount)}</span>
                </div>
              </div>
            )}
            addForm={MoneyLentForm}
          />
        </section>

        {/* Wealth History */}
        <section className="wealth-history">
          <h3>Wealth Trend</h3>
          <WealthChart history={wealthHistory} />
          <h4 className="history-list-heading">Snapshot Log</h4>
          <div className="history-list">
            {wealthHistory.length === 0 ? (
              <p className="no-items">No wealth history recorded yet</p>
            ) : (
              [...wealthHistory].sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at)).map((entry, index) => {
                const prev = [...wealthHistory].sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[index + 1];
                const change = prev ? parseFloat(entry.total_wealth) - parseFloat(prev.total_wealth) : 0;
                return (
                  <div key={entry.id || index} className="history-item">
                    <span className="history-date">
                      {new Date(entry.recorded_at).toLocaleDateString('en-IN', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <div className="history-values">
                      {prev && (
                        <span className={`history-change ${change >= 0 ? 'positive' : 'negative'}`}>
                          {change >= 0 ? '+' : ''}{formatCurrency(change)}
                        </span>
                      )}
                      <span className="history-value">{formatCurrency(entry.total_wealth)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
